import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { authFetch } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

interface RedirectOnboardingDialogProps {
  organisationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RedirectOnboardingDialog({
  organisationId,
  open,
  onOpenChange,
}: RedirectOnboardingDialogProps) {
  const defaultOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const [returnUrl, setReturnUrl] = useState(
    defaultOrigin ? `${defaultOrigin}/settings?onboarding=return` : "",
  );
  const [refreshUrl, setRefreshUrl] = useState(
    defaultOrigin ? `${defaultOrigin}/settings?onboarding=refresh` : "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setError(null);
      setSubmitting(false);
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!returnUrl.trim() || !refreshUrl.trim()) {
      setError("Both Return URL and Refresh URL are required.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authFetch(
        `/api/proxy/v1/organisations/${organisationId}/payment-account/link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            returnUrl: returnUrl.trim(),
            refreshUrl: refreshUrl.trim(),
          }),
        },
      );

      if (!response.ok) {
        const body = await response.text();
        const message = body || `Request failed (${response.status})`;
        setError(message);
        toast.error("Could not create onboarding link", { description: message });
        return;
      }

      const body = (await response.json()) as { url: string; expiresAt?: string };
      if (!body.url) {
        const message = "Response did not include a redirect URL.";
        setError(message);
        toast.error("Could not create onboarding link", { description: message });
        return;
      }

      window.location.href = body.url;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network error - please try again.";
      setError(message);
      toast.error("Could not create onboarding link", { description: message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Continue on Stripe</DialogTitle>
            <DialogDescription>
              Finish setting up payments on Stripe&apos;s own pages instead of here. You&apos;ll be
              taken to Stripe straight away, then brought back when you&apos;re done.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="redirect-return-url">Return URL</Label>
              <Input
                id="redirect-return-url"
                type="url"
                placeholder="https://app.example.com/onboarding/return"
                value={returnUrl}
                onChange={(e) => setReturnUrl(e.target.value)}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Where we sends the user once onboarding completes.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="redirect-refresh-url">Refresh URL</Label>
              <Input
                id="redirect-refresh-url"
                type="url"
                placeholder="https://app.example.com/onboarding/refresh"
                value={refreshUrl}
                onChange={(e) => setRefreshUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Where we sends the user if the link expires or they need a new one.
              </p>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting ? <Spinner className="size-4" /> : <ExternalLink className="size-4" />}
              Create link & redirect
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
